import { FormErrorText } from '@exam-management/components';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigRandomType, QConfigStatus, QConfigTimeLimitType } from '@exam-management/graphql';
import { Box, Flex, FormRadioSelection, Grid, SectionTitle, Text } from '@shared/components';
import { useMemo } from 'react';

import { useQConfigSettingsFormContext } from '../../providers';

import RandomizationLabel from './randomization-label';

interface QuestionRandomizationSectionProps {
  disabled: boolean;
}

export default function QuestionRandomizationSection(
  props: Readonly<QuestionRandomizationSectionProps>,
) {
  const { disabled: isDisabled } = props;

  const formContext = useQConfigSettingsFormContext();
  const { formState, watch } = formContext;
  const examTimingOption = watch('examTiming.option');

  const { qConfigData } = useQConfig();

  const isDraftQConfig = qConfigData.status === QConfigStatus.Draft;

  const randomizationOptions = useMemo(() => {
    return [
      {
        label: <RandomizationLabel mainLabel='เรียงตามลำดับ:' commonLabel='แสดงข้อสอบตามลำดับ' />,
        value: QConfigRandomType.Fixed,
      },
      {
        label: (
          <RandomizationLabel mainLabel='สุ่มทั้งชุด:' commonLabel='สลับตำแหน่งทุกข้อในชุดข้อสอบ' />
        ),
        value: QConfigRandomType.AllRandom,
        disabled: !isDraftQConfig || examTimingOption === QConfigTimeLimitType.ByStructure,
      },
      {
        label: (
          <RandomizationLabel
            mainLabel='สุ่มตามลำดับชั้น:'
            commonLabel='สุ่มข้อสอบที่อยู่ภายในลำดับชั้นเดียวกัน'
          />
        ),
        value: QConfigRandomType.RandomByHierarchy,
      },
    ];
  }, [examTimingOption]);

  const errorMessage = formState.errors.randomization?.root?.message;

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(8) }}>
      <SectionTitle title='Question Randomization' />
      <Grid
        sx={(theme) => ({
          gridTemplate: '1fr / 240px 1fr',
          gap: theme.spacing(4, 6),
        })}
      >
        <Box>
          <Text variant='body1Semibold'>รูปแบบการสุ่มคำถาม</Text>
        </Box>
        <Box>
          <FormRadioSelection
            name='randomization'
            options={randomizationOptions}
            sx={{
              '& .MuiButtonBase-root': {
                py: 0,
              },
              '& .MuiFormGroup-root': {
                gap: (theme) => theme.spacing(4),
              },
            }}
            disabled={!isDraftQConfig || isDisabled}
          />
          {errorMessage ? (
            <FormErrorText sx={{ mt: (theme) => theme.spacing(4) }}>{errorMessage}</FormErrorText>
          ) : null}
        </Box>
      </Grid>
    </Flex>
  );
}
